import { ChangeEvent } from 'react';

import {
  ContactHandle,
  ContactHandleType,
} from '../../../services/types/treatment-types';
import { Field, Fieldset, Label } from '../../ui/catalyst/fieldset';
import { Input } from '../../ui/catalyst/input';
import { Select } from '../../ui/catalyst/select';

interface ContactHandleFormProps {
  value: ContactHandle;
  onChange: (updatedHandle: ContactHandle) => void;
}

export const ContactHandleForm = ({
  value,
  onChange,
}: ContactHandleFormProps) => {
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value: inputValue } = e.target;
    onChange({ ...value, [name]: inputValue });
  };

  const parsePhoneAndExtension = (rawValue: string) => {
    if (!rawValue) {
      return { phoneNumber: '', extension: '' };
    }

    const match = rawValue.match(
      /^(.*?)(?:\s*(?:ext\.?|x\.?|extension)\s*[:.]?\s*([A-Za-z0-9#-]+))?\s*$/i,
    );

    if (!match) {
      return { phoneNumber: rawValue.trim(), extension: '' };
    }

    const phoneNumber = (match[1] || '').trim();
    const extension = (match[2] || '').trim();
    return { phoneNumber, extension };
  };

  const buildPhoneWithExtension = (phoneNumber: string, extension: string) => {
    const trimmedPhone = phoneNumber.trim();
    const trimmedExt = extension.trim();

    if (trimmedExt.length === 0) {
      return trimmedPhone;
    }

    if (trimmedPhone.length === 0) {
      return `ext. ${trimmedExt}`;
    }

    return `${trimmedPhone} ext. ${trimmedExt}`;
  };

  const { phoneNumber, extension } = parsePhoneAndExtension(value.value);

  const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const combined = buildPhoneWithExtension(e.target.value, extension);
    onChange({ ...value, value: combined });
  };

  const handleExtensionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const combined = buildPhoneWithExtension(phoneNumber, e.target.value);
    onChange({ ...value, value: combined });
  };

  const getPlaceholderByType = (type: ContactHandleType) => {
    switch (type) {
      case ContactHandleType.PHONE:
        return '(555) 123-4567';
      case ContactHandleType.EMAIL:
        return 'contact@example.com';
      case ContactHandleType.WEBSITE:
        return 'www.example.com';
      case ContactHandleType.FAX:
        return '(555) 123-4568';
      default:
        return 'Contact value';
    }
  };

  return (
    <Fieldset className="space-y-4 my-4">
      <Field>
        <Label htmlFor="handle_type">Contact Type</Label>
        <Select
          id="handle_type"
          name="handle_type"
          value={value.handle_type}
          onChange={handleInputChange}
        >
          <option value={ContactHandleType.PHONE}>Phone</option>
          <option value={ContactHandleType.EMAIL}>Email</option>
          <option value={ContactHandleType.WEBSITE}>Website</option>
          <option value={ContactHandleType.FAX}>Fax</option>
          <option value={ContactHandleType.OTHER}>Other</option>
        </Select>
      </Field>

      <Field>
        <Label htmlFor="name">Label (optional)</Label>
        <Input
          id="name"
          name="name"
          value={value.name || ''}
          onChange={handleInputChange}
          placeholder="e.g., Main Office, Billing Department"
        />
      </Field>

      <Field>
        <Label htmlFor="value">Value</Label>
        {value.handle_type === ContactHandleType.PHONE ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Input
                id="value_phone"
                name="value_phone"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                placeholder={getPlaceholderByType(value.handle_type)}
                aria-label="Phone number"
              />
            </div>
            <div>
              <Input
                id="value_extension"
                name="value_extension"
                value={extension}
                onChange={handleExtensionChange}
                placeholder="ext."
                aria-label="Extension"
              />
            </div>
          </div>
        ) : (
          <Input
            id="value"
            name="value"
            value={value.value}
            onChange={handleInputChange}
            placeholder={getPlaceholderByType(value.handle_type)}
          />
        )}
      </Field>
    </Fieldset>
  );
};
